/**
 * FullCostPage.js
 * ===============
 * Complete account cost view — all customers + untagged breakdown.
 * Wired to /api/v1/cost/account/* and /api/v1/cost/untagged/* endpoints.
 * Add to src/pages/FullCostPage.js and register in your nav/router.
 */

import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

// ─── Helpers ─────────────────────────────────────────────────
const fmt  = (n, d=0) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits:d, maximumFractionDigits:d })}`;
const pct  = (n)      => n == null ? '—' : `${Number(n).toFixed(1)}%`;

function getMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}
const MONTH_OPTIONS = getMonthOptions();

// ─── Styles ──────────────────────────────────────────────────
const S = {
  page:    { padding: '24px' },
  grid4:   { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 },
  grid2:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 },
  card:    { background:'#161b22', border:'1px solid #21262d', borderRadius:8, padding:20, marginBottom:16 },
  title:   { fontFamily:'monospace', fontSize:11, color:'#8b949e', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 },
  bigNum:  { fontFamily:'monospace', fontSize:22, fontWeight:700 },
  sub:     { fontFamily:'sans-serif', fontSize:11, color:'#8b949e', marginTop:2 },
  hdr:     { fontFamily:'monospace', fontSize:9, color:'#484f58', textTransform:'uppercase', letterSpacing:'0.08em' },
  row:     { padding:'8px 4px', borderTop:'1px solid #21262d', display:'grid', alignItems:'center', gap:8 },
  pill:    (c='#3fb950') => ({ display:'inline-block', fontFamily:'monospace', fontSize:10, color:c, background:`${c}18`, border:`1px solid ${c}40`, borderRadius:10, padding:'1px 8px' }),
  tag:     (c='#8b949e') => ({ fontFamily:'monospace', fontSize:10, color:c, background:`${c}15`, border:`1px solid ${c}30`, borderRadius:4, padding:'2px 7px', whiteSpace:'nowrap' }),
  btn:     (a) => ({ fontFamily:'monospace', fontSize:10, letterSpacing:'0.06em', background: a?'#1c2330':'transparent', color:a?'#e6edf3':'#8b949e', border:`1px solid ${a?'#30363d':'#21262d'}`, borderRadius:4, padding:'4px 10px', cursor:'pointer' }),
  warn:    { background:'#3d2f0a', border:'1px solid #d2992244', borderRadius:8, padding:'12px 16px', marginBottom:16 },
  loading: { color:'#8b949e', fontFamily:'monospace', fontSize:13, padding:16 },
  error:   { color:'#f85149', fontFamily:'monospace', fontSize:13, padding:16 },
};

// ─── Sub-components ───────────────────────────────────────────

function KpiCard({ label, value, sub, color='#e6edf3' }) {
  return (
    <div style={S.card}>
      <div style={S.title}>{label}</div>
      <div style={{ ...S.bigNum, color }}>{value}</div>
      {sub && <div style={S.sub}>{sub}</div>}
    </div>
  );
}

function StackedBar({ net, gross, isUntagged=false, height=8 }) {
  const saving = Math.max(gross - net, 0);
  const total  = gross || 1;
  return (
    <div style={{ background:'#21262d', borderRadius:3, height, overflow:'hidden', display:'flex' }}>
      <div style={{ flex: net/total,    background: isUntagged ? '#f85149' : '#58a6ff' }} />
      <div style={{ flex: saving/total, background: isUntagged ? '#f8514944' : '#3fb95066' }} />
    </div>
  );
}

function TaggingGauge({ taggedPct }) {
  const untaggedPct = 100 - taggedPct;
  const color = untaggedPct > 20 ? '#f85149' : untaggedPct > 10 ? '#d29922' : '#3fb950';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontFamily:'monospace', fontSize:10, color:'#8b949e' }}>Tagged</span>
        <span style={{ fontFamily:'monospace', fontSize:10, color }}>{taggedPct.toFixed(1)}%</span>
      </div>
      <div style={{ background:'#21262d', borderRadius:3, height:10, overflow:'hidden', display:'flex' }}>
        <div style={{ flex: taggedPct,   background:'#3fb950' }} />
        <div style={{ flex: untaggedPct, background: color, opacity:0.7 }} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:3 }}>
        <span style={{ fontFamily:'monospace', fontSize:9, color:'#484f58' }}>■ Tagged</span>
        <span style={{ fontFamily:'monospace', fontSize:9, color }}>{untaggedPct.toFixed(1)}% untagged</span>
      </div>
    </div>
  );
}

// ─── Untagged Drilldown Panel ─────────────────────────────────

function UntaggedPanel({ month }) {
  const [services, setServices] = useState([]);
  const [regions,  setRegions]  = useState([]);
  const [view,     setView]     = useState('service');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/api/v1/cost/untagged/services?month=${month}`),
      api.get(`/api/v1/cost/untagged/regions?month=${month}`),
    ])
      .then(([s, r]) => { setServices(s.data); setRegions(r.data); })
      .finally(() => setLoading(false));
  }, [month]);

  if (loading) return <div style={S.loading}>Loading untagged breakdown…</div>;

  const maxGross = Math.max(
    ...(view === 'service' ? services : regions).map(r => r.cost_gross), 1
  );

  return (
    <div style={{ ...S.card, borderColor:'#f8514944', background:'#f8514908' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={S.title}>⚠ Untagged Spend Breakdown</div>
        <div style={{ display:'flex', gap:6 }}>
          {['service','region'].map(v => (
            <button key={v} style={S.btn(view===v)} onClick={()=>setView(v)}>
              BY {v.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {view === 'service' ? (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'220px 1fr 100px 100px', gap:8, marginBottom:6 }}>
            {['Service','','Gross','Net'].map(h => <div key={h} style={S.hdr}>{h}</div>)}
          </div>
          {services.slice(0, 15).map((r, i) => (
            <div key={i} style={{ ...S.row, gridTemplateColumns:'220px 1fr 100px 100px' }}>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#e6edf3', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {r.aws_service}
              </div>
              <div style={{ background:'#21262d', borderRadius:3, height:6, overflow:'hidden' }}>
                <div style={{ width:`${r.cost_gross/maxGross*100}%`, height:'100%', background:'#f85149', opacity:0.7 }} />
              </div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#f85149', textAlign:'right' }}>{fmt(r.cost_gross)}</div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e',  textAlign:'right' }}>{fmt(r.cost_net)}</div>
            </div>
          ))}
          {services.length > 15 && (
            <div style={{ fontFamily:'monospace', fontSize:10, color:'#484f58', marginTop:8, paddingTop:8, borderTop:'1px solid #21262d' }}>
              +{services.length - 15} more services
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'180px 1fr 100px 100px', gap:8, marginBottom:6 }}>
            {['Region','','Gross','Net'].map(h => <div key={h} style={S.hdr}>{h}</div>)}
          </div>
          {regions.map((r, i) => (
            <div key={i} style={{ ...S.row, gridTemplateColumns:'180px 1fr 100px 100px' }}>
              <span style={S.tag('#f85149')}>{r.region}</span>
              <div style={{ background:'#21262d', borderRadius:3, height:6, overflow:'hidden' }}>
                <div style={{ width:`${r.cost_gross/maxGross*100}%`, height:'100%', background:'#f85149', opacity:0.7 }} />
              </div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#f85149', textAlign:'right' }}>{fmt(r.cost_gross)}</div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e',  textAlign:'right' }}>{fmt(r.cost_net)}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop:12, fontFamily:'sans-serif', fontSize:11, color:'#8b949e', lineHeight:1.5 }}>
        Resources above are not attributed to any customer. Add the <code style={{ fontFamily:'monospace', background:'#21262d', padding:'1px 5px', borderRadius:3 }}>Client</code> tag to resolve.
      </div>
    </div>
  );
}

// ─── Main Full Cost Page ──────────────────────────────────────

export default function FullCostPage() {
  const [summary,   setSummary]   = useState(null);
  const [allCosts,  setAllCosts]  = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[1].value);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/api/v1/cost/account/summary?month=${selectedMonth}`),
      api.get(`/api/v1/cost/account/all?month=${selectedMonth}`),
    ])
      .then(([s, a]) => { setSummary(s.data); setAllCosts(a.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={S.loading}>Loading cost data…</div>;
  if (error)   return <div style={S.error}>Error: {error}</div>;

  const hasUntagged    = allCosts.some(c => !c.is_tagged);
  const untaggedPct    = summary?.untagged_pct || 0;
  const taggedPct      = 100 - untaggedPct;
  const maxGross       = Math.max(...allCosts.map(c => c.cost_gross), 1);

  return (
    <div style={S.page}>

      {/* Month selector */}
      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
        <span style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e' }}>Month:</span>
        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
          style={{
            fontSize:13, padding:'6px 10px', border:'1px solid #21262d',
            borderRadius:6, background:'#161b22', color:'#e6edf3',
            fontFamily:'monospace', cursor:'pointer', minWidth:180
          }}
        >
          {MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Untagged warning */}
      {untaggedPct > 10 && (
        <div style={S.warn}>
          <div style={{ fontFamily:'monospace', fontSize:11, color:'#d29922', marginBottom:4 }}>
            ⚠ TAGGING COMPLIANCE — {untaggedPct.toFixed(1)}% OF SPEND UNATTRIBUTED
          </div>
          <div style={{ fontFamily:'sans-serif', fontSize:12, color:'#8b949e' }}>
            {fmt(summary?.untagged_gross)} cannot be attributed to a customer this month.
            Resources missing the <code style={{ fontFamily:'monospace' }}>Client</code> tag are shown in the breakdown below.
          </div>
        </div>
      )}

      {/* KPI row */}
      <div style={S.grid4}>
        <KpiCard label="Total Account Cost"  value={fmt(summary?.total_net,0)}      sub="net after all discounts"       color='#58a6ff' />
        <KpiCard label="Gross (List) Cost"   value={fmt(summary?.total_gross,0)}    sub="before SP + EDP"               color='#8b949e' />
        <KpiCard label="Total Savings"       value={fmt(summary?.total_saving,0)}   sub={`${pct(summary?.saving_pct)} saved`} color='#3fb950' />
        <KpiCard label="Untagged Spend"      value={fmt(summary?.untagged_gross,0)} sub={`${pct(summary?.untagged_pct)} of total`} color={untaggedPct > 10 ? '#f85149' : '#8b949e'} />
      </div>

      {/* Tagging coverage + EDP info row */}
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={S.title}>Tagging Coverage</div>
          <TaggingGauge taggedPct={taggedPct} />
        </div>
        <div style={S.card}>
          <div style={S.title}>Discount Summary</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <div style={{ fontFamily:'monospace', fontSize:10, color:'#8b949e', marginBottom:2 }}>Savings Plans</div>
              <div style={{ fontFamily:'monospace', fontSize:16, color:'#3fb950', fontWeight:700 }}>{fmt(summary?.tagged_saving,0)}</div>
            </div>
            <div>
              <div style={{ fontFamily:'monospace', fontSize:10, color:'#8b949e', marginBottom:2 }}>EDP</div>
              <div style={{ fontFamily:'monospace', fontSize:16, color:'#bc8cff', fontWeight:700 }}>{fmt(summary?.tagged_edp,0)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* All costs table */}
      <div style={S.card}>
        <div style={S.title}>All Costs — Customers + Untagged</div>
        <div style={{ display:'flex', gap:12, fontFamily:'monospace', fontSize:10, color:'#8b949e', marginBottom:12 }}>
          <span><span style={{color:'#58a6ff'}}>■</span> Net (tagged)</span>
          <span><span style={{color:'#f85149'}}>■</span> Net (untagged)</span>
          <span><span style={{color:'#3fb950'}}>■</span> Savings</span>
        </div>

        {/* Header */}
        <div style={{ display:'grid', gridTemplateColumns:'110px 1fr 100px 100px 90px 90px 70px', gap:8, marginBottom:4 }}>
          {['Customer','','Gross','Net','SP Saved','EDP','Saving'].map(h => (
            <div key={h} style={S.hdr}>{h}</div>
          ))}
        </div>

        {allCosts.map(c => (
          <div key={c.customer_code}
            style={{
              display:'grid', gridTemplateColumns:'110px 1fr 100px 100px 90px 90px 70px',
              gap:8, alignItems:'center', padding:'8px 4px',
              borderTop:'1px solid #21262d',
              background: !c.is_tagged ? '#f8514906' : 'transparent',
              cursor: c.is_tagged ? 'pointer' : 'default',
            }}
            onMouseEnter={e => { if (c.is_tagged) e.currentTarget.style.background='#1c2330'; }}
            onMouseLeave={e => { e.currentTarget.style.background = !c.is_tagged ? '#f8514906' : 'transparent'; }}
          >
            <div style={{ fontFamily:'monospace', fontSize:11, color: c.is_tagged ? '#e6edf3' : '#f85149' }}>
              {c.is_tagged ? c.customer_code : '⚠ UNTAGGED'}
            </div>
            <StackedBar net={c.cost_net} gross={c.cost_gross} isUntagged={!c.is_tagged} />
            <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e',  textAlign:'right' }}>{fmt(c.cost_gross)}</div>
            <div style={{ fontFamily:'monospace', fontSize:12, color: c.is_tagged ? '#58a6ff' : '#f85149', textAlign:'right', fontWeight:600 }}>{fmt(c.cost_net)}</div>
            <div style={{ fontFamily:'monospace', fontSize:11, color:'#3fb950',  textAlign:'right' }}>{fmt(c.cost_sp_saving)}</div>
            <div style={{ fontFamily:'monospace', fontSize:11, color:'#bc8cff',  textAlign:'right' }}>{fmt(c.cost_edp)}</div>
            <span style={S.pill(c.is_tagged ? '#3fb950' : '#f85149')}>{pct(c.saving_pct)}</span>
          </div>
        ))}
      </div>

      {/* Untagged drilldown */}
      {hasUntagged && <UntaggedPanel month={selectedMonth} />}

    </div>
  );
}
